import { useState } from "react";
import classes from "../homePageNew.module.css";
import ScoreDonut from "./ScoreDonut";
import MetricGrid from "./MetricGrid";
import KeyInsightsPanel from "./KeyInsightsPanel";
import ReportStrip from "./ReportStrip";
import FailedRunPanel from "./FailedRunPanel";
import EmptyRecordState from "./EmptyRecordState";
import SectionEmptyState from "./SectionEmptyState";
import ShareModal from "./ShareModal";
import { CategoryIcon, ShareIcon } from "./icons";
import BrandAvatar from "../../../Component/Avatar/Avatar";
import { pillClass, firstOf, resolveMetrics, llmLabel } from "../api/homePageApi";

/**
 * Generic detail pane: header (logo + name, then category · llm · market) +
 * a two-column row (donut/metrics beside Alerts) + reporting + "View full
 * <module>" button, or the failed-run panel when the selected record failed,
 * or a generic empty state when nothing is selected.
 */
export default function ModuleDetailPane({
  record,
  moduleLabel,
  noDataAtAll,
  moduleKey,
  combination,
  onRetried,
  onViewFull,
  fullViewUrl,
  userId,
  onOpenReportsModal,
  onViewReport,
}) {
  const [shareOpen, setShareOpen] = useState(false);

  if (!record) {
    return (
      <div className={classes.mdDetailPane}>
        <EmptyRecordState moduleLabel={moduleLabel} noDataAtAll={noDataAtAll} />
      </div>
    );
  }

  const label = record.brand || record.category || "Untitled";
  const status = pillClass(record.status);

  if (status === "failed") {
    return (
      <div className={classes.mdDetailPane}>
        <FailedRunPanel transactionId={record.transactionId} failReason={record.failReason} onRetried={onRetried} />
      </div>
    );
  }

  // Scores/insights live under record.metrics[] (one entry per combination);
  // resolveMetrics also falls back to the older flat shape.
  const { scores: resolvedScores, insights } = resolveMetrics(record, combination);
  const scores = resolvedScores || {};
  const subtitleParts = [firstOf(record.category), llmLabel(firstOf(record.llm)), firstOf(record.market)].filter(Boolean);
  // Keyed off the record's own data, not where it came from — search
  // results carry real scores/report now, same as the main list.
  const dataUnavailable = !resolvedScores;
  // Case-insensitive — the real API returns "Brand"/"Product" (capitalized).
  const isProduct = String(record.type || "").toLowerCase() === "product";

  return (
    <div className={classes.mdDetailPane}>
      <div className={classes.detailHeader}>
        <BrandAvatar domainName={record.brandDomain} brandName={label} />
        <div className={classes.detailHeaderText}>
          <div className={classes.detailHeaderName}>{label}</div>
          <div className={classes.detailHeaderSubtitle}>{subtitleParts.join(" · ")}</div>
        </div>
      </div>

      {dataUnavailable ? (
        <SectionEmptyState
          icon={CategoryIcon}
          title="Full scores aren't available yet"
          description="This run hasn't finished processing — full detail will show here once it's ready."
        />
      ) : (
        <div className={classes.mainRow}>
          <div className={classes.mainRowLeft}>
            <ScoreDonut
              visibility={scores.visibility_score}
              sentiment={scores.sentiment_score}
              brandPillars={scores.brand_pillars_score}
              purchaseCriteria={scores.purchase_criteria_score}
              brandIndexScore={scores.brand_index_score}
            />
            <MetricGrid
              visibility={scores.visibility_score}
              sentiment={scores.sentiment_score}
              brandPillars={scores.brand_pillars_score}
              purchaseCriteria={scores.purchase_criteria_score}
              isProduct={isProduct}
            />
          </div>
          <div className={classes.mainRowRight}>
            <KeyInsightsPanel insights={insights} />
          </div>
        </div>
      )}

      <div className={classes.detailActionsRow}>
        <button
          className={classes.viewBtn}
          onClick={() => onViewFull(record)}
          disabled={dataUnavailable && status !== "inProgress"}
          title={dataUnavailable && status !== "inProgress" ? "Full detail isn't available yet for this run" : undefined}
        >
          View {moduleLabel} →
        </button>
        {fullViewUrl && (
          <button
            className={classes.drawerActionBtn}
            onClick={() => setShareOpen(true)}
            disabled={status === "inProgress"}
            title={status === "inProgress" ? "Sharing isn't available until the run finishes" : "Share"}
          >
            <ShareIcon size={14} />
            Share
          </button>
        )}
      </div>
      <ReportStrip
        report={record.report}
        unavailable={dataUnavailable}
        onOpenReportsModal={() => onOpenReportsModal(record)}
        onViewReport={onViewReport}
        showType={false}
        moduleKey={moduleKey}
      />
      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        itemName={`${label} ${moduleLabel}`}
        documentPath={fullViewUrl ? `${window.location.origin}${fullViewUrl}` : ""}
        transactionId={record.transactionId}
        userId={userId}
      />
    </div>
  );
}
